/**
 * AuthContext — глобальный стейт текущего пользователя и access-токена.
 *
 * Архитектура:
 * - access_token хранится в памяти (`tokenStore` из api/client.ts)
 * - refresh_token — в httpOnly cookie на бэке
 * - При маунте — попытка тихого refresh, чтобы восстановить сессию
 *   после F5 (cookie живёт 7 дней)
 *
 * См. docs/ARCHITECTURE.md §4.
 */

import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError, authApi, tokenStore } from '@/api/client';
import type { components } from '@/api/schema';

type User = components['schemas']['User'];

export interface AuthState {
  user: User | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthState['status']>('loading');

  // При маунте пробуем тихо восстановить сессию
  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      try {
        const tokenResponse = await authApi.refresh();
        tokenStore.set(tokenResponse.access_token);
        const me = await authApi.me();
        if (cancelled) return;
        setUser(me);
        setStatus('authenticated');
      } catch {
        if (cancelled) return;
        tokenStore.set(null);
        setUser(null);
        setStatus('anonymous');
      }
    };

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const response = await authApi.login({ username, password });
    tokenStore.set(response.access_token);
    setUser(response.user);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (err) {
      // Даже если бэк недоступен — локально разлогиниваемся
      if (!(err instanceof ApiError)) throw err;
    } finally {
      tokenStore.set(null);
      setUser(null);
      setStatus('anonymous');
      // Догашиваем сессию в Keycloak; при выключенном SSO бэк вернёт на /login.
      window.location.assign('/api/auth/sso/logout');
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, status, login, logout }),
    [user, status, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
